import * as React from 'react';
import { type ReactNode, type Ref } from 'react';
import { type AriaTextFieldProps } from 'react-aria';
import { type ConformInputProps } from '../../utils/conformInputPropsToAria';

import { ControlledTextInput } from './components/ControlledTextInput';
import { Icon } from './components/Icon';

import { UncontrolledTextInput } from './components/UncontrolledTextInput';

// Usage Notes:
// ariaLabel required if no label prop is given (no visible label in the UI)
// placeholder only visible if no label prop is given (no visible label in the UI)

export const LABEL_OFFSET = 8;

interface InitialProps {
  helpText?: string;
  inputProps?: ConformInputProps & { 'data-testid'?: string };
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  errorMessage?: string | string[];
  iconLeft?: ReactNode;
  validateOnBlur?: (e: string) => Promise<void> | void;
  showCharacterCount?: boolean;
  className?: string;
  isFocused?: boolean;
  isZipcode?: boolean;
  textInputRef?: Ref<HTMLDivElement>;
  initialMasked?: boolean;
  onInvalid?: () => void;
}

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };
type XOR<T, U> = T | U extends object
  ? (Without<T, U> & U) | (Without<U, T> & T)
  : T | U;

export type UncontrolledTextInput = InitialProps &
  Omit<AriaTextFieldProps, 'value' | 'onChange'>;
export type ControlledTextInput = InitialProps &
  Omit<AriaTextFieldProps, 'defaultValue'>;

export type Props = XOR<UncontrolledTextInput, ControlledTextInput>;

export function TextInput(props: Props) {
  if ('value' in props && 'onChange' in props) {
    return <ControlledTextInput {...props} />;
  }

  return <UncontrolledTextInput {...props} />;
}

TextInput.IconLeft = Icon;
TextInput.Icon = Icon;
